import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import Card from '../UI/Card';
import Button from '../UI/Button';
import Input from '../UI/Input';

export default function BoardCreate() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: 'COLLABORATION',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const categories = [
    { value: 'COLLABORATION', label: '협업 제안', description: '다른 교사와 협업 프로젝트 제안' },
    { value: 'JOINT_CLASS', label: '공동 수업', description: '공동 수업 운영 제안 및 계획' },
    { value: 'RESOURCE_SHARE', label: '자료 공유', description: '수업 자료 및 교안 공유' },
    { value: 'QUESTION', label: '질문', description: '궁금한 점이나 조언 요청' },
    { value: 'ANNOUNCEMENT', label: '공지사항', description: '중요한 공지 및 안내' },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = '제목을 입력해주세요.';
    } else if (formData.title.length > 200) {
      newErrors.title = '제목은 200자를 초과할 수 없습니다.';
    }

    if (!formData.content.trim()) {
      newErrors.content = '내용을 입력해주세요.';
    } else if (formData.content.length > 5000) {
      newErrors.content = '내용은 5000자를 초과할 수 없습니다.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);
      const response = await api.post('/board', formData);

      if (response.data.success) {
        navigate(`/board/${response.data.post.id}`);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || '게시글 작성에 실패했습니다.';
      setErrors({ submit: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-neutral-800 mb-2">게시글 작성</h1>
        <p className="text-neutral-600">교사들과 소통하고 협업하세요.</p>
      </div>

      <Card>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-3">
              카테고리 선택
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {categories.map((cat) => (
                <button
                  key={cat.value}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, category: cat.value }))}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    formData.category === cat.value
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-neutral-200 hover:border-neutral-300'
                  }`}
                >
                  <p className="font-medium text-neutral-800 mb-1">{cat.label}</p>
                  <p className="text-sm text-neutral-600">{cat.description}</p>
                </button>
              ))}
            </div>
          </div>

          <Input
            label="제목"
            name="title"
            value={formData.title}
            onChange={handleChange}
            error={errors.title}
            placeholder="제목을 입력하세요"
            required
          />

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              내용
              <span className="text-danger-500 ml-1">*</span>
            </label>
            <textarea
              name="content"
              value={formData.content}
              onChange={handleChange}
              className="w-full px-4 py-2.5 rounded-xl border-2 border-neutral-200 focus:border-primary-500 focus:outline-none transition-colors duration-200 min-h-[300px]"
              placeholder="내용을 입력하세요..."
            />
            {errors.content && (
              <p className="mt-1.5 text-sm text-danger-500">{errors.content}</p>
            )}
            <p className="mt-1.5 text-sm text-neutral-500">
              {formData.content.length} / 5000자
            </p>
          </div>

          {errors.submit && (
            <div className="p-4 bg-danger-50 border border-danger-200 rounded-xl">
              <p className="text-sm text-danger-600">{errors.submit}</p>
            </div>
          )}

          <div className="flex gap-3">
            <Button type="submit" disabled={loading} className="flex-1">
              {loading ? '작성 중...' : '게시글 작성'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/board')}
              disabled={loading}
            >
              취소
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
